import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Star, ShieldCheck, Phone, Navigation, ShoppingCart, Store, Clock, Bike, Footprints, MessageSquare } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

const TEAL = "#005C66";

function openDirections(p) {
  if (p.latitude && p.longitude) {
    window.open(`https://www.google.com/maps/search/?api=1&query=${p.latitude},${p.longitude}`, "_blank");
  }
}

export default function FindPharmacies() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [onboarded, setOnboarded] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [error, setError] = useState("");

  const fetchPharmacies = useCallback((lat, lng) => {
    api
      .get("/places/pharmacies-combined", { params: { lat, lng, radius_km: 15 } })
      .then((res) => {
        if (res.data?.success) {
          setOnboarded(res.data.onboarded || []);
          setNearby(res.data.nearby || []);
        }
      })
      .catch(() => setError("Could not load pharmacies. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError("Location is not available in this browser.");
      setLoading(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchPharmacies(pos.coords.latitude, pos.coords.longitude),
      () => {
        setError("Please allow location access to find pharmacies near you.");
        setLoading(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }, [fetchPharmacies]);

  const renderOnboarded = (p) => (
    <Card key={p.id} data-testid={`pharmacy-onboarded-${p.id}`} className="rounded-3xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-semibold truncate">{p.name}</p>
            <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-lg" style={{ backgroundColor: "#E0F2F1", color: TEAL }}>
              <ShieldCheck className="h-3 w-3" /> On Trata
            </span>
          </div>
          {p.address && <p className="text-sm text-muted-foreground mt-0.5 truncate">{p.address}{p.city ? `, ${p.city}` : ""}</p>}
          <div className="flex items-center flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
            {p.distanceKm != null && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.distanceKm} km</span>}
            {p.is24x7 && <span className="flex items-center gap-1 text-green-600"><Clock className="h-3.5 w-3.5" /> 24x7</span>}
            <span className={`flex items-center gap-1 ${p.homeDelivery ? "text-blue-600" : ""}`}>
              {p.homeDelivery ? <Bike className="h-3.5 w-3.5" /> : <Footprints className="h-3.5 w-3.5" />}
              {p.homeDelivery ? `Delivery${p.deliveryRadiusKm ? ` ≤ ${p.deliveryRadiusKm}km` : ""}` : "Pickup only"}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button data-testid={`pharmacy-order-${p.id}`} onClick={() => navigate(`/place-order?pharmacyId=${p.id}&pharmacyName=${encodeURIComponent(p.name)}`)} className="flex-1 rounded-xl text-white" style={{ backgroundColor: TEAL }}>
          <ShoppingCart className="h-4 w-4 mr-1.5" /> Order Medicine
        </Button>
        {p.phone && (
          <Button data-testid={`pharmacy-call-${p.id}`} variant="outline" size="icon" className="rounded-xl" onClick={() => window.open(`tel:${p.phone}`)}>
            <Phone className="h-4 w-4" style={{ color: TEAL }} />
          </Button>
        )}
        <Button data-testid={`pharmacy-dir-${p.id}`} variant="outline" size="icon" className="rounded-xl" onClick={() => openDirections(p)}>
          <Navigation className="h-4 w-4" style={{ color: TEAL }} />
        </Button>
      </div>
    </Card>
  );

  const renderNearby = (p) => (
    <Card key={p.id} data-testid={`pharmacy-nearby-${p.id}`} className="rounded-3xl p-4">
      <p className="font-semibold truncate">{p.name}</p>
      {p.address && <p className="text-sm text-muted-foreground mt-0.5 truncate">{p.address}</p>}
      <div className="flex items-center flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
        {p.distanceKm != null && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.distanceKm} km</span>}
        {p.rating != null && <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 text-amber-500" /> {p.rating}</span>}
        {p.isOpen === true && <span className="text-green-600">Open now</span>}
        {p.isOpen === false && <span className="text-red-600">Closed</span>}
      </div>
      <div className="mt-3">
        <Button data-testid={`pharmacy-dir-${p.id}`} variant="outline" className="rounded-xl" onClick={() => openDirections(p)}>
          <Navigation className="h-4 w-4 mr-1.5" style={{ color: TEAL }} /> Directions
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 pb-24">
      <div className="flex items-center gap-2 py-4 sticky top-0 bg-background z-10">
        <button onClick={() => navigate(-1)} data-testid="pharmacies-back" className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-accent">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-2xl font-semibold">Pharmacies</h1>
        <button onClick={() => navigate("/pharmacy-inbox")} data-testid="pharmacies-inbox" className="ml-auto h-10 w-10 flex items-center justify-center rounded-full hover:bg-accent">
          <MessageSquare className="h-5 w-5" style={{ color: TEAL }} />
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-3xl" />)}
        </div>
      ) : error ? (
        <Card className="rounded-3xl p-6 text-center text-muted-foreground">{error}</Card>
      ) : (
        <div className="space-y-6">
          <section>
            <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-3" style={{ color: TEAL }}>
              <ShieldCheck className="h-4 w-4" /> On Trata Health {onboarded.length ? `(${onboarded.length})` : ""}
            </h2>
            <div className="space-y-3">
              {onboarded.length ? onboarded.map(renderOnboarded) : (
                <Card className="rounded-3xl p-4 text-sm text-muted-foreground">No Trata-partnered pharmacies near you yet. You can still reach nearby pharmacies below.</Card>
              )}
            </div>
          </section>

          <section>
            <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-3 text-muted-foreground">
              <Store className="h-4 w-4" /> Pharmacies near you {nearby.length ? `(${nearby.length})` : ""}
            </h2>
            <div className="space-y-3">
              {nearby.length ? nearby.map(renderNearby) : (
                <Card className="rounded-3xl p-4 text-sm text-muted-foreground">No other pharmacies found nearby.</Card>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
